import { action, observable } from 'mobx';
import { IgenerateRecPitPostData } from 'cd-eft/models/SurveyEngineering/generateRecPit';
import { IgenerateRoundPitPostData } from 'cd-eft/models/SurveyEngineering/generateRoundPit';
import { IgenerateDikedPitPostAData } from 'cd-eft/models/SurveyEngineering/generateDikedPit';
import stores from '../../../models/Stores';
import { MapView } from 'cd-eft/utils/esriTypes';
import { removeLayers, isPointInsidePolygon, getBoundaryPolygon } from 'cd-eft/utils/gisUtils';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { createSaveThumbnail } from 'cd-eft/utils/structureLayerUtils';


const designController = SETAPI.DesignElementController;
const structureController = SETAPI.StructureController;
const { pitDesignStore, designStore, projectsLibraryStore } = stores.AppStore;

export class NewPitVM {
  private mapView: MapView;
  private baseGraphicSketch: SketchViewModel;
  private baseGraphicLayer: GraphicsLayer;
  private mapBlurHandler: IHandle;
  private surfaceModified: boolean = false;
  props;
  constructor(props: any) {
    this.props = props;

    this.mapView = props.mapView;
    if (this.mapView && this.mapView.map.findLayerById('baseStructureGraphics'))
      this.mapView.map.remove(this.mapView.map.findLayerById('baseStructureGraphics'));
    this.baseGraphicLayer = new GraphicsLayer({ id: 'baseStructureGraphics', title: 'Base Structure Graphic' });
    this.mapView.map.add(this.baseGraphicLayer);
    this.baseGraphicSketch = new SketchViewModel({
      view: this.mapView,
      layer: this.baseGraphicLayer,
      defaultUpdateOptions: {
        enableScaling: false,
        enableRotation: true,
        toggleToolOnClick: false,
        preserveAspectRatio: false,
        tool: 'transform',
      },
    });
    this.setSketchUpdateEvent();
    this.mapBlurHandler = this.setMapBlurEvent();
  }

  @observable sketchCompleteOrder: 'none' | 'cancel' = 'none';
  @observable error: boolean = false;
  @observable errorMsg: string = '';
  @observable slopeErrorMsg: string = '';
  @observable isLoading: boolean = false;
  @observable isRound: boolean = false;
  @observable isRect: boolean = false;
  @observable isDiked: boolean = false;
  @observable usePitDepth: boolean = true;
  @observable sketchActive: boolean = false;
  private readonly drawInputs: string[] = ['bottomWidth', 'bottomLength', 'length', 'width', 'radius', 'orienAzimuth'];

  @observable newPitForm = pitDesignStore.newDesignForm;

  // Form Validation model
  @observable formValidation = {
    formErrors: {
      x: '',
      y: '',
      z: '',
      name: '',
      type: '',
      width: '',
      bottomWidth: '',
      bottomLength: '',
      bottomElevation: '',
      topElevation: '',
      topWidth: '',
      length: '',
      orienAzimuth: '',
      'slope-1': '',
      'slope-2': '',
      'slope-3': '',
      'slope-4': '',
      'pit-cut-slope-1': '',
      'pit-cut-slope-2': '',
      'pit-cut-slope-3': '',
      'pit-cut-slope-4': '',
      'pit-fill-slope-1': '',
      'pit-fill-slope-2': '',
      'pit-fill-slope-3': '',
      'pit-fill-slope-4': '',
      'pit-side-slope-1': '',
      'pit-side-slope-2': '',
      'pit-side-slope-3': '',
      'pit-side-slope-4': '',
      radius: '',
    },
    xValid: false,
    yValid: false,
    zValid: false,
    nameValid: false,
    typeValid: false,
    widthValid: false,
    bottomWidthValid: false,
    bottomLengthValid: false,
    topWidthValid: false,
    bottomElevationValid: false,
    topElevationValid: false,
    lengthValid: false,
    orienAzimuthValid: false,
    'slope-1Valid': false,
    'slope-2Valid': false,
    'slope-3Valid': false,
    'slope-4Valid': false,
    'pit-cut-slope-1Valid': false,
    'pit-cut-slope-2Valid': false,
    'pit-cut-slope-3Valid': false,
    'pit-cut-slope-4Valid': false,
    'pit-fill-slope-1Valid': false,
    'pit-fill-slope-2Valid': false,
    'pit-fill-slope-3Valid': false,
    'pit-fill-slope-4Valid': false,
    'pit-side-slope-1Valid': false,
    'pit-side-slope-2Valid': false,
    'pit-side-slope-3Valid': false,
    'pit-side-slope-4Valid': false,
    radiusValid: false,
    formValid: false,
  };

  @action.bound onMount(history?: any) {
    pitDesignStore.showLoader = false;
    if (pitDesignStore.newDesignForm.type === 'Round Pit') {
      this.isRound = true;
      this.isRect = false;
      this.isDiked = false;
      if (this.baseGraphicSketch) {
        this.baseGraphicSketch.defaultUpdateOptions.enableRotation = false;
      }
    } else {
      if (this.baseGraphicSketch) {
        this.baseGraphicSketch.defaultUpdateOptions.enableRotation = true;
      }
    }
    if (pitDesignStore.newDesignForm.type === 'Rectangular Pit') {
      this.isRect = true;
      this.isRound = false;
      this.isDiked = false;
    }
    if (pitDesignStore.newDesignForm.type === 'Diked Pit') {
      this.isDiked = true;
      this.isRound = false;
      this.isRect = false;
    }
    // Set formValidation values that do not apply to whichever pit type is selected
    this.exclude();

    if (!designStore.hasProject()) {
      // pitDesignStore.showNotification(designStore.noProjectMessage, 'Uh oh!', 'warn');
      MessageCenter.addWarning(designStore.noProjectMessage, [
        {
          callback: () => {
            history?.push('/projects/new-project');
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Project',
        },
      ]);
    } else if (!designStore.hasSurfaceTest()) {
      // pitDesignStore.showNotification(designStore.noSurfaceMessage, 'Uh oh!', 'warn');
      MessageCenter.addWarning(designStore.noSurfaceMessage, [
        {
          callback: () => {
            history?.push(`/design/${designStore.projectsStore.currentProject.projectUuid}/surface`);
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Surface',
        },
      ]);
    } else {
      // pitDesignStore.clearNotification();
      MessageCenter.clearMessages();
      MessageCenter.fillMessageCacheByOriginalCodes([
        'Pit_03', 'Pit_06', 'Pit_07', 'Pit_08', 'Pit_09', 'Pit_10', 'Pit_11', 'Pit_12', 'Pad_16', 'Common_08', 'Common_15',
      ]);
    }
  }
  @action.bound onUnmount() {
    // mapView Potentially undefined before this unmount
    if (this.mapView && this.mapView.map) {
      this.mapView.map.remove(this.baseGraphicLayer);
      this.props.removeCenterMarker();
    }
    this.mapBlurHandler.remove();
    this.baseGraphicSketch.destroy();
    if (pitDesignStore.chooseCenter) {
      pitDesignStore.chooseCenter = false;
      document.body.style.cursor = 'default';
    }
  }

  @action.bound async handleInputChange(event) {
    let drawMoveRotateBase: boolean = false;
    const { target } = event;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const { name, id } = target;
    // if value is number keep it a number
    if (!isNaN(parseFloat(value)) && isFinite(value) && !target.id.includes('name')) {
      value = +value;
    }

    this.checkStructurePendingEdits(id);

    if (id.includes('center')) {
      this.newPitForm.center[name] = value;
      drawMoveRotateBase = true;
    } else if (id.includes('slope')) {
      this.setSlopeValue(id, value);
    } else {
      this.newPitForm[name] = value;
      if (
        this.drawInputs.find((input) => {
          return input === name;
        })
      ) {
        drawMoveRotateBase = true;
      }
    }
    if (id.includes('pit-type')) {
      this.props.removeBaseStructureGraphics();
      pitDesignStore.newDesignForm[name] = value;
      this.onMount();
    }

    if (id.includes('slope') && !this.isDiked && this.newPitForm.equalSlopes) {
      await this.validateField(target.id, value);
      this.shareSlopeValidation(target.id, 'slope');
    } else if (id.includes('cut-slope') && this.newPitForm.equalCutSlopes) {
      await this.validateField(target.id, value);
      this.shareSlopeValidation(target.id, 'pit-cut-slope');
    } else if (id.includes('fill-slope') && this.newPitForm.equalFillSlopes) {
      await this.validateField(target.id, value);
      this.shareSlopeValidation(target.id, 'pit-fill-slope');
    } else if (id.includes('side-slope') && this.newPitForm.equalSideSlopes) {
      await this.validateField(target.id, value);
      this.shareSlopeValidation(target.id, 'pit-side-slope');
    } else {
      await this.validateField(name, value);
    }

    if (drawMoveRotateBase && (id.includes('center') || this.formValidation[name + 'Valid'])) {
      if (pitDesignStore.newDesignForm.type === 'Round Pit') {
        if (pitDesignStore.newDesignForm.radius > 0) {
          this.props.drawBaseGraphic(pitDesignStore.newDesignForm, true);
        }
      } else {
        this.props.drawBaseGraphic(pitDesignStore.newDesignForm, false);
      }
    }
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (checkNameDuplicate) {
      return;
    }
  }

  @action.bound shareSlopeValidation(originSlope: string, slopeGroup: string) {
    for (let i = 1; i < 5; i++) {
      const name = slopeGroup + `-${i}`;
      this.formValidation.formErrors[name] = this.formValidation.formErrors[originSlope];
      this.formValidation[name + 'Valid'] = this.formValidation[originSlope + 'Valid'];
    }
    this.slopeErrorMsg = this.getSlopeErrorMessage();
    this.validateForm();
  }

  @action.bound handlePitDepthChange(event) {
    const { target } = event;
    let { value } = target;

    if (!isNaN(parseFloat(value)) && isFinite(value)) {
      value = +value;
    }
    this.handleInputChange(event);

    if (this.usePitDepth) {
      this.newPitForm.topElevation = this.newPitForm.bottomElevation + this.newPitForm.pitDepth;
      this.newPitForm.topElevation = +(+this.newPitForm.topElevation).toFixed(3);
    } else {
      this.newPitForm.pitDepth = this.newPitForm.topElevation - +this.newPitForm.bottomElevation;
    }
  }
  // Toggle Slope Functions
  @action.bound toggleEqualSlopes() {
    this.newPitForm.equalSlopes = !this.newPitForm.equalSlopes;
    // make slopes equal on form
    if (this.newPitForm.equalSlopes) {
      this.newPitForm.slopes.forEach((value, i) => {
        this.newPitForm.slopes[i] = this.newPitForm.slopes[0];
        this.validateField(`slope-${i + 1}`, this.newPitForm.slopes[0]);
      });
    }
  }

  @action.bound toggleEqualSideSlopes() {
    this.newPitForm.equalSideSlopes = !this.newPitForm.equalSideSlopes;
    // make slopes equal on form
    if (this.newPitForm.equalSideSlopes) {
      this.newPitForm.sideSlopes.forEach((value, i) => {
        this.newPitForm.sideSlopes[i] = this.newPitForm.sideSlopes[0];
        this.validateField(`pit-side-slope-${i + 1}`, this.newPitForm.sideSlopes[0]);
      });
    }
  }

  @action.bound toggleEqualCutSlopes() {
    this.newPitForm.equalCutSlopes = !this.newPitForm.equalCutSlopes;
    // make slopes equal on form
    if (this.newPitForm.equalCutSlopes) {
      this.newPitForm.cutSlopes.forEach((value, i) => {
        this.newPitForm.cutSlopes[i] = this.newPitForm.cutSlopes[0];
        this.validateField(`pit-cut-slope-${i + 1}`, this.newPitForm.cutSlopes[0]);
      });
    }
  }

  @action.bound toggleEqualFillSlopes() {
    this.newPitForm.equalFillSlopes = !this.newPitForm.equalFillSlopes;
    // make slopes equal on form
    if (this.newPitForm.equalFillSlopes) {
      this.newPitForm.fillSlopes.forEach((value, i) => {
        this.newPitForm.fillSlopes[i] = this.newPitForm.fillSlopes[0];
        this.validateField(`pit-fill-slope-${i + 1}`, this.newPitForm.fillSlopes[0]);
      });
    }
  }

  @action.bound toggleUsePitDepth() {
    this.usePitDepth = !this.usePitDepth;
  }

  @action.bound async checkStructurePendingEdits(inputId) {
    pitDesignStore.pendingEdits.unsavedEdits = true;
    if (pitDesignStore.newDesign || pitDesignStore.editDesign) {
      if (inputId === 'name') {
        pitDesignStore.pendingEdits.name = true;
      } else {
        pitDesignStore.pendingEdits.design = true;
      }
    }
  }

  @action.bound async checkNameDuplicate() {
    const nameDuplicate = pitDesignStore.pitSummaryTable.some((summary) => {
      return summary.designElementName.toLowerCase() === pitDesignStore.newDesignForm.name.toLowerCase() &&
        pitDesignStore.newDesignForm.designElementId !== summary.designElementId && pitDesignStore.newDesignForm.type === summary.designElementTypeId.name;
    });
    if (nameDuplicate) {
      /*       const errorCode = 'Common_07';
            const fieldName = 'name'
            await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName); */
      await MessageCenter.addErrorFromCode('Common_07');
      return true;
    }
    return false;
  }

  @action.bound async checkFormInputValues() {
    let isValid = true;
    const fieldsToCheck = ['x', 'y', 'z'];

    for (const fieldName of fieldsToCheck) {
      if (this.newPitForm.center[fieldName] === 0) {
        const errorCode = 'Common_08';
        await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName);
        isValid = false;
      }
    }
    return isValid;
  }

  @action.bound clearCenterErrorMessages() {
    for (const fieldName of ['x', 'y', 'z', 'bottomElevation', 'topElevation']) {
      this.formValidation.formErrors[fieldName] = '';
      this.formValidation[`${fieldName}Valid`] = true;
    }
  }
  @action.bound async generateStructure() {
    this.exclude();
    this.sketchCompleteOrder = 'cancel';
    // check form validation - defaults to false until interacted with

    // Set formValid to false by default
    this.formValidation.formValid = false;

    if (!this.formValidation.formValid) {
      pitDesignStore.showLoader = true;
      this.isLoading = true;
      this.error = false;
      this.errorMsg = '';
      // Loop validates field if good in place of user interaction\
      for (const field in this.formValidation.formErrors) {
        const value = this.newPitForm[field];
        if (field.includes('slope-1') && !this.isDiked && !field.includes('pit-')) {
          this.newPitForm.slopes.forEach(async (value, i) => {
            await this.validateField('slope-' + (+i + 1), value);
          });
        } else if (field.includes('cut-slope-1') && this.isDiked) {
          this.newPitForm.cutSlopes.forEach(async (value, i) => {
            await this.validateField('pit-cut-slope-' + (+i + 1), value);
          });
        } else if (field.includes('fill-slope-1') && this.isDiked) {
          this.newPitForm.fillSlopes.forEach(async (value, i) => {
            await this.validateField('pit-fill-slope-' + (+i + 1), value);
          });
        } else if (field.includes('side-slope-1') && this.isDiked) {
          this.newPitForm.sideSlopes.forEach(async (value, i) => {
            await this.validateField('pit-side-slope-' + (+i + 1), value);
          });
        } else if (!field.includes('slope')) {
          await this.validateField(field, value);
        } else {
          this.formValidation[`${field}Valid`] = true;
          this.formValidation.formErrors[field] = '';
          this.validateForm();
        }
      }
      const checkNameDuplicate = await this.checkNameDuplicate();
      const checkFormInputValues = await this.checkFormInputValues();

      // If invalid or Name Duplicate Exists - set formValid to false and halt execution
      if (!this.formValidation.formValid || checkNameDuplicate || !checkFormInputValues) {
        this.formValidation.formValid = false;
        this.isLoading = false;
        pitDesignStore.showLoader = false;
        return;
      } else {
        this.formValidation.formValid = true;
      }
    }
    const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
    const x = this.newPitForm.center.x;
    const y = this.newPitForm.center.y;

    const pointInsidePolygon = await isPointInsidePolygon(x, y, bPolygon, projectsLibraryStore.projectSpatialReference!);

    if (!pointInsidePolygon) {
      MessageCenter.addErrorFromCode('Elev_01');
      pitDesignStore.showLoader = false;
      this.isLoading = false;
      return;
    }

    if (this.isRect) {
      console.warn('createRecPit');
      await this.createRecPit();
    } else if (this.isRound) {
      console.warn('createRoundPit');
      await this.createRoundPit();
    } else if (this.isDiked) {
      console.warn('createDikedPit');
      await this.createDikedPit();
    }
  }

  @action.bound async createDikedPit() {
    const postDikedJSON: IgenerateDikedPitPostAData = {
      name: this.newPitForm.name,
      type: this.newPitForm.type,
      equalSideSlopes: this.newPitForm.equalSideSlopes,
      equalCutSlopes: this.newPitForm.equalCutSlopes,
      equalFillSlopes: this.newPitForm.equalFillSlopes,
      center: this.newPitForm.center,
      width: this.newPitForm.bottomWidth,
      length: this.newPitForm.bottomLength,
      orienAzimuth: this.newPitForm.orienAzimuth,
      topWidth: this.newPitForm.topWidth,
      topElevation: this.newPitForm.topElevation,
      designElementId: designStore.currentSurface.designElementId,
      sideSlopes: this.newPitForm.sideSlopes,
      cutSlopes: this.newPitForm.cutSlopes,
      fillSlopes: this.newPitForm.fillSlopes,
    };

    // Center z is actually bottomElevation and if we are using pitDepth we calculate that below
    postDikedJSON.center.z = this.newPitForm.bottomElevation;
    if (this.usePitDepth) {
      postDikedJSON.topElevation = this.newPitForm.bottomElevation + this.newPitForm.pitDepth;
    }
    pitDesignStore.showLoader = true;
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';

    try {
      const response = (await structureController.generateDikedPitUsingPOST(postDikedJSON)).data;
      if (response.designElementId && this.mapView) {
        pitDesignStore.newPitResults = response;
        pitDesignStore.newDesign = true;
        designStore.currentDesignElementJson = response as any;
        designStore.currentDesignElementJson.designedContours = null;
        designStore.currentDesignElementJson.designedTinTriangles = null;

        removeLayers([pitDesignStore.currentDesignLayer], this.mapView);
        const surfaceModified = await DesignContainerVM.loadSurfaceFromData(
          response.designedTinTriangles!,
          undefined,
          response.designedContours
        );
        this.surfaceModified = this.surfaceModified || surfaceModified;
        pitDesignStore.addDikedPitToMap(response, this.mapView, true, projectsLibraryStore.projectSpatialReference!);
        this.props.removeCenterMarker();
      }
      if (response.warningMsg) {
        // pitDesignStore.showNotification(response.warningMsg, 'Warning', 'warn');
        MessageCenter.addWarning(response.warningMsg);
      } else {
        //new design is generated successfully based on pending edits
        pitDesignStore.pendingEdits.name = false;
        pitDesignStore.pendingEdits.design = false;
        pitDesignStore.pendingEdits.unsavedEdits = true;
        // pitDesignStore.clearNotification();
        MessageCenter.clearMessages();
      }
      this.isLoading = false;
      pitDesignStore.showLoader = false;
      this.error = false;
      designStore.structureLayerExists = true;
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {        
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      }
      else {
        MessageCenter.clearMessages();
        // Check if the error is a TypeError from EftAuthAxiosInterceptor
        if (typeof error === 'object' && error !== null && error.toString().includes("TypeError: Cannot read properties of undefined")) {
          await MessageCenter.addErrorFromCode('Pit_24');
        }
      }
      this.isLoading = false;
      pitDesignStore.showLoader = false;
      this.error = true;
    }
  }

  @action.bound async createRoundPit() {
    const postRoundJSON: IgenerateRoundPitPostData = {
      name: this.newPitForm.name,
      type: this.newPitForm.type,
      center: this.newPitForm.center,
      radius: this.newPitForm.radius,
      designElementId: designStore.currentSurface.designElementId,
      slope: this.newPitForm.slopes[0],
    };

    pitDesignStore.showLoader = true;
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';

    try {
      const response = (await structureController.generateRoundPitUsingPOST(postRoundJSON)).data;
      if (response.designElementId && this.mapView) {
        pitDesignStore.newPitResults = response;
        designStore.currentDesignElementJson = response as any;
        designStore.currentDesignElementJson.designedContours = null;
        designStore.currentDesignElementJson.designedTinTriangles = null;
        removeLayers([pitDesignStore.currentDesignLayer], this.mapView);
        const surfaceModified = await DesignContainerVM.loadSurfaceFromData(
          response.designedTinTriangles!,
          undefined,
          response.designedContours
        );
        this.surfaceModified = this.surfaceModified || surfaceModified;
        pitDesignStore.addPitToMap(
          response,
          this.mapView,
          true,
          projectsLibraryStore.projectSpatialReference!,
          pitDesignStore.originalIdString ? pitDesignStore.originalIdString : undefined
        );
        pitDesignStore.newDesign = true;
        this.props.removeCenterMarker();
      }
      if (response.warningMsg) {
        // pitDesignStore.showNotification(response.warningMsg, 'Warning', 'warn');
        MessageCenter.addWarning(response.warningMsg);
      } else {
        //new design is generated successfully based on pending edits
        pitDesignStore.pendingEdits.name = false;
        pitDesignStore.pendingEdits.design = false;
        pitDesignStore.pendingEdits.unsavedEdits = true;
        // pitDesignStore.clearNotification();
        MessageCenter.clearMessages();
      }
      this.isLoading = false;
      pitDesignStore.showLoader = false;
      this.error = false;
      designStore.structureLayerExists = true;
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      } else {
        MessageCenter.addError((error as any).message);
      }
      this.error = true;
      this.isLoading = false;
      pitDesignStore.showLoader = false;
    }
  }

  @action.bound async createRecPit() {
    const postRectJSON: IgenerateRecPitPostData = {
      name: this.newPitForm.name,
      type: this.newPitForm.type,
      equalSlopes: this.newPitForm.equalSlopes,
      center: this.newPitForm.center,
      width: this.newPitForm.width,
      length: this.newPitForm.length,
      orienAzimuth: this.newPitForm.orienAzimuth,
      designElementId: designStore.currentSurface.designElementId,
      slopes: this.newPitForm.slopes,
    };

    pitDesignStore.showLoader = true;
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';

    try {
      const response = (await structureController.generateRecPitUsingPOST(postRectJSON)).data;
      if (response.designElementId && this.mapView) {
        pitDesignStore.newPitResults = response;
        designStore.currentDesignElementJson = response as any;
        designStore.currentDesignElementJson.designedContours = null;
        designStore.currentDesignElementJson.designedTinTriangles = null;
        removeLayers([pitDesignStore.currentDesignLayer], this.mapView);
        const surfaceModified = await DesignContainerVM.loadSurfaceFromData(
          response.designedTinTriangles!,
          undefined,
          response.designedContours
        );
        this.surfaceModified = this.surfaceModified || surfaceModified;
        pitDesignStore.addPitToMap(
          response,
          this.mapView,
          true,
          projectsLibraryStore.projectSpatialReference!,
          pitDesignStore.originalIdString ? pitDesignStore.originalIdString : undefined,
        );
        pitDesignStore.newDesign = true;
        this.props.removeCenterMarker();
      }
      if (response.warningMsg) {
        // pitDesignStore.showNotification(response.warningMsg, 'Warning', 'warn');
        // Match the warning message with common warning messages
        if (response.warningMsg.includes("Structure extends beyond surveyed area")) {
            MessageCenter.addWarning(response.warningMsg, undefined, "Common_01");
        } else {
            MessageCenter.addWarning(response.warningMsg);
        }
      } else {
        // pitDesignStore.clearNotification();
        //new design is generated successfully based on pending edits
        pitDesignStore.pendingEdits.name = false;
        pitDesignStore.pendingEdits.design = false;
        pitDesignStore.pendingEdits.unsavedEdits = true;
        MessageCenter.clearMessages();
      }
      this.isLoading = false;
      pitDesignStore.showLoader = false;
      this.error = false;
      designStore.structureLayerExists = true;
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      } else {
        MessageCenter.addError((error as any).message);
      }
      this.error = true;
      this.isLoading = false;
      pitDesignStore.showLoader = false;
    }
  }

  // @action.bound pickUpCenter() {
  //   // Want this to create map event to allow user to click on map and get cords
  //   if (pitDesignStore.chooseCenter) {
  //     pitDesignStore.chooseCenter = false;
  //     document.body.style.cursor = 'default';
  //   } else {
  //     pitDesignStore.chooseCenter = true;
  //     document.body.style.cursor = 'crosshair';
  //   }
  // }

  @action.bound handleOnBlurRounding(event) {
    const { target } = event;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const { name, id } = target;
    // if value is number keep it a number
    if (!isNaN(parseFloat(value)) && isFinite(value) && !target.id.includes('name')) {
      value = +value;
    }
    const adjustedValue = toDecimalPrecision(value, 3);

    if (id.includes('center')) {
      this.newPitForm.center[name] = adjustedValue;
    } else if (id.includes('slope')) {
      this.setSlopeValue(id, adjustedValue);
    } else {
      this.newPitForm[name] = adjustedValue;
    }
    this.validateField(name, adjustedValue);
  }

  @action.bound resetOriginalPitDesignLayer() {
    if (
      pitDesignStore.originalDesignLayer &&
      pitDesignStore.editDesign &&
      pitDesignStore.currentDesignLayer !== pitDesignStore.originalDesignLayer
    ) {
      const index = pitDesignStore.pitLayers.findIndex((layer) => layer.id === pitDesignStore.currentDesignLayer.id);
      // pitDesignStore.pitLayers[index] = pitDesignStore.originalDesignLayer;
      pitDesignStore.pitLayers.splice(index, 1);
      removeLayers([pitDesignStore.currentDesignLayer], this.mapView);
      // pitDesignStore.currentDesignLayer = pitDesignStore.originalDesignLayer;
      pitDesignStore.newPitResults = pitDesignStore.originalPitResults;
      pitDesignStore.addPitToMap(pitDesignStore.newPitResults, this.mapView, true, projectsLibraryStore.projectSpatialReference!, pitDesignStore.originalIdString);
    }
  }

  @action.bound async cancelAddDesignElement(resetMostRecent: boolean = false, cancelEdits: boolean = true) {
    designStore.onNewStructureTab = false;
    designStore.hideCrossSectionToolbar = true;
    designStore.hideSingleCrossSection = true;
    this.sketchCompleteOrder = 'cancel';
    if (cancelEdits) this.resetOriginalPitDesignLayer();

    const summaryEntry = (pitDesignStore.pitSummaryTable as any[]).find(
      (entry) => entry.designElementId.toString() === pitDesignStore.currentDesignLayer.id
    );
    if (this.mapView) {
      if (!summaryEntry || !summaryEntry.checked) removeLayers([pitDesignStore.currentDesignLayer], this.mapView);
      this.props.removeBaseStructureGraphics();
      this.props.removeCenterMarker();
      if (resetMostRecent && this.surfaceModified) {
        this.surfaceModified = !(await DesignContainerVM.displayOriginalSurface());
      }
      if (!designStore.hideSingleCrossSection) await designStore.validateSingleCrossSection(this.mapView);
    }
    designStore.currentDesignElementJson = null;
    designStore.hideCrossSectionToolbar = true;
    designStore.hideSingleCrossSection = true;
    pitDesignStore.cancelDesignElement();
  }
  @action.bound async updateDesignElement() {
    designStore.currentDesignElementJson = null;
    this.sketchCompleteOrder = 'cancel';
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (checkNameDuplicate) {
      return;
    }
    if (pitDesignStore.pendingEdits.name) {
      pitDesignStore.newPitResults.name = pitDesignStore.newDesignForm.name;
    }
    // these fields should be set to null for the back end.
    const newPitResultsCopy = pitDesignStore.newPitResults;
    newPitResultsCopy.designedContours = null;
    newPitResultsCopy.designedTinTriangles = null;
    pitDesignStore.showLoader = true;
    this.isLoading = true;

    const postJson = {
      surfaceDefId: designStore.currentSurface.surfaceDefId,
      designElementId: pitDesignStore.newDesignForm.designElementId,
      designElementName: pitDesignStore.newDesignForm.name,
      sessionId: designStore.projectsStore.currentSessionId,
      designElementTypeId: 0,
      json: JSON.stringify(newPitResultsCopy),
    };

    if (this.isRect) {
      postJson.designElementTypeId = 11;
    }
    if (this.isRound) {
      postJson.designElementTypeId = 12;
    }
    if (this.isDiked) {
      postJson.designElementTypeId = 13;
    }

    this.error = false;

    try {
      const response = (await designController.updateDesignElementUsingPOST(postJson)).data;
      if (response.designElementId && this.mapView) {
        removeLayers([pitDesignStore.currentDesignLayer], this.mapView);
        const label = (await structureController.generateConstructionReportJsonByDesignElementJsonIdAndDesignElementTypeIdUsingPOST(
          response.designElementId,
          postJson.designElementTypeId,
        )).data;
        pitDesignStore.addPitToMap(
          pitDesignStore.newPitResults,
          this.mapView,
          false,
          projectsLibraryStore.projectSpatialReference!,
          response.designElementId.toString(),
          label
        );
        pitDesignStore.latestElementId = response.designElementId;
        this.cancelAddDesignElement(false, false);
        //all changes are saved and updated on map after user clicked the accept edits button
        pitDesignStore.pendingEdits.unsavedEdits = false;

        createSaveThumbnail(this.mapView).then((snapShot: any) => {
          this.saveThumbnail(snapShot, response.designElementId, response.designElementTypeId)
        });
      }
      this.isLoading = false;
      pitDesignStore.showLoader = false;
    } catch (error) {
      this.error = true;
      console.error('Error:', error);
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      } else {
        MessageCenter.addError((error as any).message);
      }
      this.isLoading = false;
      pitDesignStore.showLoader = false;
    }
  }

  @action.bound async postAddDesignElement() {
    designStore.onNewStructureTab = false;
    designStore.currentDesignElementJson = null;
    this.sketchCompleteOrder = 'cancel';
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (checkNameDuplicate) {
      return;
    }
    // these fields should be set to null for the back end.
    const newPitResultsCopy = pitDesignStore.newPitResults;
    newPitResultsCopy.designedContours = null;
    newPitResultsCopy.designedTinTriangles = null;
    pitDesignStore.showLoader = true;
    this.isLoading = true;

    const addDesignData = {
      surfaceDefId: designStore.currentSurface.surfaceDefId,
      designElementName: pitDesignStore.newPitResults.name,
      sessionId: designStore.projectsStore.currentSessionId,
      designElementTypeId: 0,
      json: JSON.stringify(newPitResultsCopy),
    };

    if (this.isRect) {
      addDesignData.designElementTypeId = 11;
    }
    if (this.isRound) {
      addDesignData.designElementTypeId = 12;
    }
    if (this.isDiked) {
      addDesignData.designElementTypeId = 13;
    }
    const postJSON = addDesignData;
    postJSON.json = JSON.stringify(pitDesignStore.newPitResults);
    this.error = false;
    this.errorMsg = '';
    try {
      const response = (await designController.addDesignElementUsingPOST(postJSON)).data;
      if (response.designElementId && this.mapView) {
        removeLayers([pitDesignStore.currentDesignLayer], this.mapView);
        const label = (await structureController.generateConstructionReportJsonByDesignElementJsonIdAndDesignElementTypeIdUsingPOST(
          response.designElementId,
          addDesignData.designElementTypeId,
        )).data;
        pitDesignStore.addPitToMap(
          pitDesignStore.newPitResults,
          this.mapView,
          false,
          projectsLibraryStore.projectSpatialReference!,
          response.designElementId.toString(),
          label
        );
        pitDesignStore.latestElementId = response.designElementId;
        pitDesignStore.cancelDesignElement();
        designStore.structureLayerExists = true;

        createSaveThumbnail(this.mapView).then((snapShot: any) => {
          this.saveThumbnail(snapShot, response.designElementId, response.designElementTypeId);
        });

        pitDesignStore.displayedPitsIds = [];
        pitDesignStore.displayedPitsIds.push(response.designElementId);
      }

      this.error = false;
      this.errorMsg = '';
      //all changes are saved and updated on map after user clicked the save button
      pitDesignStore.pendingEdits.unsavedEdits = false;
      this.isLoading = false;
      pitDesignStore.showLoader = false;
    } catch (error) {
      this.error = true;
      console.error('Error:', error);
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      } else {
        MessageCenter.addError((error as any).message);
      }
      this.isLoading = false;
      pitDesignStore.showLoader = false;
    }
  }

  // form validation by field
  @action.bound async validateField(fieldName, value) {
    // connects form field to validation table
    const keyValid = fieldName + 'Valid';
    let fieldValid = this.formValidation[keyValid];
    const fieldValidationErrors = this.formValidation.formErrors;
    let errorCode: string = '';
    let skipCheck = false;
    switch (fieldName) {
      case 'slope-1':
      case 'slope-2':
      case 'slope-3':
      case 'slope-4':
        fieldValid = value > 0 || this.isDiked;
        fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_03';
        break;
      case 'pit-cut-slope-1':
      case 'pit-cut-slope-2':
      case 'pit-cut-slope-3':
      case 'pit-cut-slope-4':
      case 'pit-fill-slope-1':
      case 'pit-fill-slope-2':
      case 'pit-fill-slope-3':
      case 'pit-fill-slope-4':
      case 'pit-side-slope-1':
      case 'pit-side-slope-2':
      case 'pit-side-slope-3':
      case 'pit-side-slope-4':
        fieldValid = value > 0 || this.isRound || this.isRect;
        fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_03';
        break;
      case 'width':
        fieldValid = value > 0 || this.isRound || this.isDiked;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'common_03';
        break;
      case 'length':
        fieldValid = value > 0 || this.isRound || this.isDiked;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'common_04';
        break;
      case 'bottomWidth':
        fieldValid = (value > 0 && Number.isInteger(+value)) || this.isRound || this.isRect;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_21';
        break;
      case 'bottomLength':
        fieldValid = (value > 0 && Number.isInteger(+value)) || this.isRound || this.isRect;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_22';
        break;
      case 'bottomElevation':
        fieldValid = value !== 0 || this.isRound || this.isRect;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_08';
        break;
      case 'topWidth':
        fieldValid = (value > 0 && Number.isInteger(+value)) || this.isRound || this.isRect;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_20';
        break;
      case 'topElevation':
        fieldValid = value !== 0 || this.isRound || this.isRect;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_10';
        break;
      case 'name':
        fieldValid = value !== '';
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_11';
        break;
      case 'orienAzimuth':
        fieldValid = (value >= 0 && value <= 360) || this.isRound;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  '; // This message is the same for pits and pads
        errorCode = fieldValid ? '' : 'Pad_16';
        break;
      case 'radius':
        fieldValid = (value > 0 && Number.isInteger(+value)) || this.isDiked || this.isRect;
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'Pit_23';
        break;
      case 'type':
        fieldValid = value !== '';
        // fieldValidationErrors[fieldName] = fieldValid ? '' : '  ';
        errorCode = fieldValid ? '' : 'common_01';
        break;
      case 'x':
        if (value === '' || value === 0) {
          fieldValid = false;
          errorCode = 'Common_08';
        } else {
          fieldValid = true;
          errorCode = '';
        }
        break;
      case 'y':
        if (value === '' || value === 0) {
          fieldValid = false;
          errorCode = 'Common_08';
        } else {
          fieldValid = true;
          errorCode = '';
        }
        break;
      case 'z':
        if (value === '' || value === 0) {
          fieldValid = false;
          errorCode = 'Common_08';
        } else {
          fieldValid = true;
          errorCode = '';
        }
        break;
      default:
        skipCheck = true;
        break;
    }

    this.formValidation.formErrors = fieldValidationErrors;
    if (!skipCheck) await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName);
    this.formValidation[keyValid] = fieldValid;
    this.validateForm();
  }

  @action.bound async getErrorMessage(code: string, container, fieldName: string) {
    if (code === '') {
      container[fieldName] = '';
    } else {
      const message = await MessageCenter.addErrorLabelFromCode(code);
      container[fieldName] = message;
    }
    if (fieldName.includes('slope')) {
      this.slopeErrorMsg = this.getSlopeErrorMessage();
    }
    return;
  }

  @action.bound exclude() {
    if (this.isRect) {
      for (let i = 0; i < 5; i++) {
        this.formValidation[`pit-cut-slope-${i}Valid`] = true;
        this.formValidation[`pit-fill-slope-${i}Valid`] = true;
        this.formValidation[`pit-side-slope-${i}Valid`] = true;
      }
    }
    if (this.isRound) {
      for (let i = 1; i < 5; i++) {
        this.formValidation[`slope-${i}Valid`] = true;
      }
      for (let i = 0; i < 5; i++) {
        this.formValidation[`pit-cut-slope-${i}Valid`] = true;
        this.formValidation[`pit-fill-slope-${i}Valid`] = true;
        this.formValidation[`pit-side-slope-${i}Valid`] = true;
      }
    }
    if (this.isDiked) {
      for (let i = 0; i < 5; i++) {
        this.formValidation[`slope-${i}Valid`] = true;
      }
    }
  }

  // checks if there are any error msgs over entire form
  @action.bound validateForm() {
    let valid = true;
    for (const key in this.formValidation.formErrors) {
      if (!this.formValidation[key + 'Valid']) {
        valid = false;
      }
    }
    this.formValidation.formValid = valid;
  }

  @action.bound errorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage.length === 0 ? '' : ' usa-input--error';
  }

  @action.bound getSlopeErrorMessage() {
    let slopeErrorMessage = '';
    if (this.isRound) {
      slopeErrorMessage = this.formValidation.formErrors['slope-1'];
    } else if (this.isRect) {
      for (let i = 1; i < 5; i++) {
        if (this.formValidation.formErrors['slope-' + i] !== '') {
          slopeErrorMessage = this.formValidation.formErrors['slope-' + i];
          break;
        }
      }
    } else if (this.isDiked) {
      for (let i = 1; i < 5; i++) {
        if (this.formValidation.formErrors['pit-cut-slope-' + i] !== '') {
          slopeErrorMessage = this.formValidation.formErrors['pit-cut-slope-' + i];
          break;
        } else if (this.formValidation.formErrors['pit-fill-slope-' + i] !== '') {
          slopeErrorMessage = this.formValidation.formErrors['pit-fill-slope-' + i];
          break;
        } else if (this.formValidation.formErrors['pit-side-slope-' + i] !== '') {
          slopeErrorMessage = this.formValidation.formErrors['pit-side-slope-' + i];
          break;
        }
      }
    }
    return slopeErrorMessage;
  }

  // END Form Validation model

  @action.bound activateMoveRotateSketch() {
    if (this.baseGraphicLayer.graphics.length === 1 && !this.sketchActive) {
      this.baseGraphicSketch.update(this.baseGraphicLayer.graphics.getItemAt(0));
      this.sketchActive = true;
    } else {
      if (this.baseGraphicSketch === undefined) {
        this.initializeBaseSketch();
      }
      this.drawBase();
    }
  }

  private drawBase() {
    const form = pitDesignStore.newDesignForm;
    if (form.center.x > 0 && form.center.y > 0) {
      if (form.type === 'Round Pit' && form.radius > 0) {
        this.props.drawBaseGraphic(pitDesignStore.newDesignForm, true);
        this.baseGraphicSketch.update(this.baseGraphicLayer.graphics.getItemAt(0));
        this.sketchActive = true;
      } else if (form.type === 'Rectangular Pit' && form.width > 0 && form.length > 0) {
        this.props.drawBaseGraphic(pitDesignStore.newDesignForm, false);
        this.baseGraphicSketch.update(this.baseGraphicLayer.graphics.getItemAt(0));
        this.sketchActive = true;
      } else if (form.type === 'Diked Pit' && form.bottomWidth > 0 && form.bottomLength > 0) {
        this.props.drawBaseGraphic(pitDesignStore.newDesignForm, false);
        this.baseGraphicSketch.update(this.baseGraphicLayer.graphics.getItemAt(0));
        this.sketchActive = true;
      } else {
        this.sketchActive = false;
      }
    } else {
      this.sketchActive = false;
    }
  }

  private initializeBaseSketch() {
    if (this.mapView && this.mapView.map.findLayerById('baseStructureGraphics'))
      this.mapView.map.remove(this.mapView.map.findLayerById('baseStructureGraphics'));
    this.baseGraphicLayer = new GraphicsLayer({ id: 'baseStructureGraphics', title: 'Base Structure Graphic' });
    this.mapView.map.add(this.baseGraphicLayer);
    this.baseGraphicSketch = new SketchViewModel({
      view: this.mapView,
      layer: this.baseGraphicLayer,
      defaultUpdateOptions: {
        enableScaling: false,
        enableRotation: true,
        toggleToolOnClick: false,
        preserveAspectRatio: false,
        tool: 'transform',
      },
    });
    this.setSketchUpdateEvent();
    this.mapBlurHandler = this.setMapBlurEvent();
  }

  private setSketchUpdateEvent() {
    this.baseGraphicSketch.on('update', async (event) => {
      const eventInfo = event.toolEventInfo;
      if (eventInfo && eventInfo.type === 'scale-start') {
        this.baseGraphicSketch.complete();
        return;
      }
      if (event.state.includes('start')) {
        this.sketchActive = true;
      }
      if (
        event.state !== 'complete' &&
        eventInfo &&
        (eventInfo.type === 'move-stop' || eventInfo.type === 'rotate-stop')
      ) {
        if (pitDesignStore.newDesignForm.type === 'Round Pit') {
          this.props.updateBaseGraphic(pitDesignStore.newDesignForm, true);
        } else {
          this.props.updateBaseGraphic(pitDesignStore.newDesignForm, false);
        }
      }
      if (event.state === 'complete') {
        if (pitDesignStore.currentTab === 'New Pit') {
          switch (this.sketchCompleteOrder) {
            case 'none':
              // console.log('no special case');
              this.generateStructure();
              this.sketchActive = false;
              break;
            case 'cancel':
              // console.log('cancel case');
              this.sketchActive = false;
              break;
          }
          this.sketchCompleteOrder = 'none';
        } else {
          this.sketchActive = false;
        }
      }
    });
  }

  private setMapBlurEvent() {
    return this.mapView.on('blur', async () => {
      // console.log('map blur PRE-timeout');
      if (this.baseGraphicSketch.state === 'active') {
        await setTimeout(() => {
          // console.log('map blur POST-timeout');
          this.baseGraphicSketch.complete();
        }, 300);
      }
    });
  }
  //  condition block from input handler for all the different slopes
  private setSlopeValue(id, value) {
    // console.log('setSlopeValue value: ', value);
    if (id.includes('slope') && !this.isDiked) {
      if (this.newPitForm.equalSlopes) {
        this.newPitForm.slopes.forEach((v, i) => {
          this.newPitForm.slopes[i] = value;
        });
      } else {
        const index = id.replace('slope-', '');
        this.newPitForm.slopes[+index - 1] = value;
      }
    } else if (id.includes('cut-slope')) {
      if (this.newPitForm.equalCutSlopes) {
        this.newPitForm.cutSlopes.forEach((v, i) => {
          this.newPitForm.cutSlopes[i] = value;
        });
      } else {
        const index = id.replace('pit-cut-slope-', '');
        this.newPitForm.cutSlopes[+index - 1] = value;
      }
    } else if (id.includes('fill-slope')) {
      if (this.newPitForm.equalFillSlopes) {
        this.newPitForm.fillSlopes.forEach((v, i) => {
          this.newPitForm.fillSlopes[i] = value;
        });
      } else {
        const index = id.replace('pit-fill-slope-', '');
        this.newPitForm.fillSlopes[+index - 1] = value;
      }
    } else if (id.includes('side-slope')) {
      if (this.newPitForm.equalSideSlopes) {
        this.newPitForm.sideSlopes.forEach((v, i) => {
          this.newPitForm.sideSlopes[i] = value;
        });
      } else {
        const index = id.replace('pit-side-slope-', '');
        this.newPitForm.sideSlopes[+index - 1] = value;
      }
    }
  }

  async saveThumbnail(snapShot: any, designElementId: number | undefined, designElementTypeId: number | undefined) {
    if (snapShot && designElementTypeId !== undefined && designElementId !== undefined) {
      const payloadForMap = {
        designElementId: designElementId,
        designElementTypeId: designElementTypeId,
        profileImageBase64String: undefined,
        profileOrCrossSection: undefined,
        stationId: undefined,
        structureMapImageBase64String: snapShot.dataUrl,
        structureProfilesId: 0,
        twoDimensionalGeometryJson: undefined
      };

      try {
        await SETAPI.StructureProfileController.generateUpdateStructureMapImageUsingPOST(payloadForMap);
      } catch (error) {
        throw error;
      }
    }
  }
  /**
   * user leaving notification section
   */
  @observable notification: any = {
    openPit: '',
  };

  @action.bound async getErrors() {
    let message = await MessageCenter.addErrorLabelFromCode('Survey_21'); // 'Please Save or Cancel Pending Edits before Leaving Surface Design.';
    message = message.replace('Surface', 'Pit');
    this.notification.openPit = message;
  }

  @action.bound userLeavingWarningOnOk() {
    return false;
  }
}
